import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const NewCart = () => {
  const { items, total, removeItem, updateQuantity, clear } = useCart();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const validateEmail = (email: string): boolean => {
    if (!email || typeof email !== 'string') return false;
    email = email.trim();
    if (email === '') return false;
    
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(email);
  };

  const handleContinue = async () => {
    // Validate email
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    setEmailError('');
    
    if (!items || items.length === 0) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Create a simplified order object with necessary fields for checkout
      const orderData = {
        name: items[0].title,
        quantity: items[0].quantity,
        price: items[0].price,
        image: items[0].image
      };
      
      // Create a temporary order ID
      const tempOrderId = `temp_${Date.now()}`;
      
      // Store items in session storage for checkout
      sessionStorage.setItem('checkout_items', JSON.stringify([orderData]));
      sessionStorage.setItem('checkout_email', email);
      sessionStorage.setItem('checkout_total', total.toString());
      sessionStorage.setItem('checkout_order_id', tempOrderId);
      
      // If there's only one item, store the store ID
      let storeId = null;
      if (items.length > 0) {
        storeId = items[0].floristId || items[0].storeId;
        if (storeId) {
          sessionStorage.setItem('checkout_store_id', storeId);
        }
      }
      
      // Navigate to delivery details
      navigate(`/delivery-details/${tempOrderId}`);
    } catch (error) {
      console.error('Error preparing checkout:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-accent pt-24 pb-12">
      <div className="max-w-6xl mx-auto px-4">
        <h1 className="text-3xl font-semibold mb-8">Your Cart</h1>
        
        {/* Progress Steps */}
        <div className="mb-10">
          <div className="flex items-center justify-between max-w-2xl mx-auto bg-white/50 backdrop-blur-sm rounded-lg p-6 shadow-sm">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-[#967F81] text-white flex items-center justify-center text-sm font-medium shadow-md">1</div>
              <div className="ml-3">
                <p className="text-sm font-medium">Cart</p>
                <p className="text-xs text-gray-600">Review your items</p>
              </div>
            </div>
            <div className="flex-1 mx-4 h-px bg-gray-200/70"></div>
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center text-sm font-medium shadow">2</div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Delivery</p>
                <p className="text-xs text-gray-600">Enter delivery details</p>
              </div>
            </div>
            <div className="flex-1 mx-4 h-px bg-gray-200/70"></div>
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-gray-200 text-gray-600 flex items-center justify-center text-sm font-medium shadow">3</div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Payment</p>
                <p className="text-xs text-gray-600">Complete payment</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-8">
          <div className="col-span-2">
            <Card className="bg-white shadow-lg border-0 ring-1 ring-black/5">
              <CardHeader className="border-b border-gray-100 bg-white rounded-t-xl">
                <CardTitle>Items in Your Cart</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {items && items.length > 0 ? (
                  <div className="space-y-4">
                    {items.map((item) => (
                      <div key={item.id} className="flex justify-between items-center py-4 border-b border-gray-100">
                        <div className="flex items-center space-x-4">
                          <div className="w-20 h-20 rounded-lg bg-gray-100 overflow-hidden">
                            <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                          </div>
                          <div>
                            <h3 className="font-medium">{item.title}</h3>
                            <p className="text-sm text-gray-500">Price: ${item.price.toFixed(2)}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center border rounded-md">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => updateQuantity(item.id, Math.max(1, item.quantity - 1))}
                              className="h-8 px-2"
                            >
                              -
                            </Button>
                            <span className="w-8 text-center">{item.quantity}</span>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              className="h-8 px-2"
                            >
                              +
                            </Button>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => removeItem(item.id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    ))}
                    
                    <div className="mt-6">
                      <Button
                        variant="outline"
                        onClick={() => clear()}
                      >
                        Clear Cart
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500 mb-4">Your cart is empty</p>
                    <Button onClick={() => navigate('/')}>Continue Shopping</Button>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {items && items.length > 0 && (
              <Card className="bg-white shadow-lg border-0 ring-1 ring-black/5 mt-6">
                <CardHeader className="border-b border-gray-100 bg-white rounded-t-xl">
                  <CardTitle>Continue to Checkout</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email address"
                        className={`mt-1 ${emailError ? 'border-red-500' : ''}`}
                      />
                      {emailError && (
                        <p className="text-sm text-red-500 mt-1">{emailError}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        We'll send your order confirmation to this email
                      </p>
                    </div>
                    
                    <div className="mt-6">
                      <Button
                        onClick={handleContinue}
                        disabled={isLoading || !items || items.length === 0}
                        className="w-full py-6"
                      >
                        {isLoading ? 'Processing...' : `Continue to Delivery Details - $${total?.toFixed(2)}`}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
          
          <div className="col-span-1">
            <Card className="bg-white shadow-lg border-0 ring-1 ring-black/5">
              <CardHeader className="border-b border-gray-100">
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 p-6">
                {items && items.length > 0 ? (
                  <>
                    <div className="space-y-1">
                      {items.map((item) => (
                        <div key={item.id} className="flex justify-between text-sm">
                          <span>{item.title} (x{item.quantity})</span>
                          <span>${(item.price * item.quantity).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    
                    <Separator />
                    
                    <div className="pt-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Subtotal</span>
                        <span className="font-medium">${total?.toFixed(2)}</span>
                      </div>
                      
                      <div className="flex justify-between text-base font-medium mt-4">
                        <span>Total</span>
                        <span>${total?.toFixed(2)}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-gray-500 text-center">No items in cart</p>
                )}
              </CardContent>
            </Card>
            
            <div className="mt-6 bg-white rounded-lg shadow-lg p-6 border-0 ring-1 ring-black/5">
              <h3 className="font-medium mb-3">Need Help?</h3>
              <p className="text-sm text-gray-600 mb-4">
                If you have any questions or need assistance with your order, please contact our customer support.
              </p>
              <div className="text-sm">
                <p className="text-gray-800">Email: support@marketbloom.com</p>
                <p className="text-gray-800">Phone: (555) 123-4567</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewCart; 